'use strict';

/**
 * Maps a pipeline-status fact to the Level 1 pizza-tracker step label for that pipeline.
 * Bind keys come from the PortalApi graph catalog composed with local presentation.
 */

import {
	CORE_PROGRESS_STAGE_LABELS,
	KITS_PROGRESS_STAGE_LABELS,
	MERGED_PROGRESS_STAGE_LABELS,
	PIPELINE_PROGRESS_STAGE_LABELS,
} from './pipelineProgressCopy';

const PIPELINE_BY_NUMBER = Object.freeze(['V7', 'Core', 'Kits', 'Merged', 'DiscoveryPanels']);

const HIGH_LEVEL_LABELS = Object.freeze({
	...PIPELINE_PROGRESS_STAGE_LABELS,
	...CORE_PROGRESS_STAGE_LABELS,
	...KITS_PROGRESS_STAGE_LABELS,
	...MERGED_PROGRESS_STAGE_LABELS,
});

/** @param {unknown} pipeline */
export function normalizeActivityPipeline(pipeline) {
	if (pipeline == null || pipeline === '') return '';
	const raw = String(pipeline).trim();
	if (/^\d+$/.test(raw)) {
		return PIPELINE_BY_NUMBER[Number(raw)] ?? raw;
	}
	return raw;
}

function bindKey(pipeline, service, stage) {
	return `${normalizeActivityPipeline(pipeline)}|${String(service ?? '').trim()}|${String(stage ?? '').trim()}`;
}

/**
 * @param {import('./pipelineStatusTypes').PipelineStatusGraphDefinition[]} definitions
 * @returns {Map<string, { id: string, label: string }>}
 */
export function activityStageBindingsFromDefinitions(definitions) {
	/** @type {Map<string, { id: string, label: string }>} */
	const byKey = new Map();
	for (const graph of definitions ?? []) {
		for (const stage of graph.stages ?? []) {
			const pipeline = stage.factMatch?.pipeline;
			const service = stage.factMatch?.service ?? stage.service;
			const producerStage = stage.factMatch?.stage;
			const highLevelId = stage.highLevelStage;
			if (!pipeline || !service || !producerStage || !highLevelId) continue;
			const label = HIGH_LEVEL_LABELS[highLevelId];
			if (!label) continue;
			byKey.set(bindKey(pipeline, service, producerStage), { id: highLevelId, label });
		}
	}
	return byKey;
}

/**
 * @param {Record<string, unknown>} row
 * @param {Map<string, { id: string, label: string }> | import('./pipelineStatusTypes').PipelineStatusGraphDefinition[]} catalog
 * @returns {{ id: string, label: string } | null}
 */
export function activityHighLevelStep(row, catalog) {
	if (!row || catalog == null) return null;
	const bindings = catalog instanceof Map ? catalog : activityStageBindingsFromDefinitions(catalog);
	const hit = bindings.get(bindKey(row.pipeline, row.service, row.stage));
	return hit ?? null;
}

/**
 * @param {Record<string, unknown>} row
 * @returns {{ projectCode?: string, sampleSetName?: string }}
 */
export function activityDisplayNames(row) {
	if (!row) return {};
	const raw = row.detailsJson ?? row.DetailsJson ?? row.details;
	let details = raw;
	if (typeof raw === 'string' && raw.trim()) {
		try {
			details = JSON.parse(raw);
		} catch {
			return {};
		}
	}
	if (!details || typeof details !== 'object') return {};
	const projectCode =
		details.projectCode ?? details.ProjectCode;
	const sampleSetName =
		details.sampleSetName ?? details.SampleSetName;
	return {
		projectCode:
			projectCode != null && String(projectCode).trim() !== ''
				? String(projectCode).trim()
				: undefined,
		sampleSetName:
			sampleSetName != null && String(sampleSetName).trim() !== ''
				? String(sampleSetName).trim()
				: undefined,
	};
}
